import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import {
  workerInviteExperienceSchema,
  workerInvitePrefillResponseSchema,
  workerInviteSummarySchema,
  workerInvitesResponseSchema,
} from "@/schemas/workerInvites";
import type {
  WorkerInviteExperience,
  WorkerInviteSummary,
  WorkerInvitePrefillResponse,
  WorkerInvitesRequest,
  WorkerInvitesResponse,
} from "@/schemas/workerInvites";

import { QueryKeys } from "../queryKeys";

import { apiRequest } from "./apiUtils";

const WORKER_INVITES_QUERY_KEY = ["worker-invites"] as const;

/** Submit the owner's worker list; the BE dedups members and sends invites. */
export async function sendWorkerInvites(
  body: WorkerInvitesRequest,
): Promise<WorkerInvitesResponse> {
  const res = await apiRequest<unknown>("owner-onboarding/me/worker-invites", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return workerInvitesResponseSchema.parse(res);
}

export function useSendWorkerInvites() {
  const queryClient = useQueryClient();
  return useMutation<WorkerInvitesResponse, Error, WorkerInvitesRequest>({
    mutationFn: sendWorkerInvites,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: WORKER_INVITES_QUERY_KEY });
      // Existing PublicMarket workers go straight onto the roster.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ROSTER] });
    },
  });
}

/** Workers the current owner has invited, newest first (Your Roster). */
export async function listWorkerInvites(): Promise<Array<WorkerInviteSummary>> {
  const res = await apiRequest<unknown>("owner-onboarding/me/worker-invites", {
    method: "GET",
  });
  return z.array(workerInviteSummarySchema).parse(res);
}

export function useWorkerInvitesQuery() {
  return useQuery({
    queryKey: WORKER_INVITES_QUERY_KEY,
    queryFn: listWorkerInvites,
  });
}

/**
 * Resolve an invite token into pre-population data for the signup form. Public
 * (unauthenticated) endpoint; throws ApiError (404) when the token is invalid or
 * expired so callers can silently fall back to a blank form.
 */
export async function getWorkerInvitePrefill(
  token: string,
): Promise<WorkerInvitePrefillResponse> {
  const res = await apiRequest<unknown>(
    `public/worker-invites/${encodeURIComponent(token)}`,
    { method: "GET" },
  );
  return workerInvitePrefillResponseSchema.parse(res);
}

/**
 * The experience the inviting restaurant entered, if the current worker signed
 * up through an invite link; null otherwise (the BE answers 204).
 */
export async function getMyInviteExperience(): Promise<WorkerInviteExperience | null> {
  const res = await apiRequest<unknown>("onboarding/me/invite", {
    method: "GET",
  });
  return res ? workerInviteExperienceSchema.parse(res) : null;
}

export function useMyInviteExperienceQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["worker-invites", "me"],
    queryFn: getMyInviteExperience,
    enabled,
    staleTime: Infinity,
  });
}
